import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Wifi, Smartphone, Volume2, Battery } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const OpenersPage = () => {
  const openerTypes = [
    {
      title: "Belt Drive Openers",
      description: "Smooth, quiet operation perfect for attached garages in Saskatoon homes.",
      features: ["Ultra-quiet operation", "Smooth lifting", "Low maintenance", "Ideal for attached garages"]
    },
    {
      title: "Chain Drive Openers",
      description: "Durable and reliable, great for heavy doors in Saskatoon.",
      features: ["Heavy-duty performance", "Long-lasting", "Cost-effective", "Works in cold weather"]
    },
    {
      title: "Screw Drive Openers",
      description: "Minimal moving parts for dependable service in Saskatoon.",
      features: ["Few moving parts", "Reliable operation", "Less maintenance", "Strong lifting power"]
    },
    {
      title: "Smart Openers",
      description: "Wi-Fi connected openers with app control for modern Saskatoon homeowners.",
      features: ["Smartphone control", "Wi-Fi connectivity", "Real-time alerts", "Voice assistant compatible"]
    }
  ];

  const features = [
    { icon: Wifi, title: "Wi-Fi Connectivity", desc: "Control your door from anywhere" },
    { icon: Smartphone, title: "Smartphone App", desc: "Open, close, monitor remotely" },
    { icon: Volume2, title: "Quiet Operation", desc: "Belt drive for silent performance" },
    { icon: Battery, title: "Battery Backup", desc: "Works during power outages" }
  ];

  const faqItems = [
    {
      q: "How long do garage door openers last?",
      a: "Quality openers typically last 10-15 years with proper maintenance. Saskatoon homeowners should service openers annually for best performance."
    },
    {
      q: "What type of opener is quietest?",
      a: "Belt drive openers are the quietest option, making them perfect for Saskatoon homes with attached garages or living spaces above the garage."
    },
    {
      q: "Can I add smart features to my existing opener?",
      a: "In many cases, yes! We can install smart adapters or recommend a modern opener with built-in Wi-Fi for Saskatoon homeowners."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Garage Door Openers Saskatoon</h1>
            <p className="text-xl text-blue-100 mb-8">Professional opener installation & repair serving Saskatoon homeowners</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 text-lg">
                <Phone size={20} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-8 py-6 text-lg">
                <MessageSquare size={20} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 mb-4">
              A garage door opener should make daily life easier in Saskatoon. When the right unit is installed, your door moves with better control and less strain. <strong>306 Garage Doors</strong> helps Saskatoon homeowners choose openers that fit the weight of the door, layout of the garage, and the way the space is used.
            </p>
            <p className="text-lg text-gray-700">
              Some Saskatoon families want a quieter unit for an attached garage, while others need dependable lifting power for a heavier setup. Our job is to make sure the opener is genuinely well-suited to your Saskatoon home.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Opener Types for Saskatoon Homes</h2>
            <p className="text-xl text-gray-600">Choose the right opener for your needs</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {openerTypes.map((type, idx) => (
              <Card key={idx} className="border-2 hover:border-blue-600 hover:shadow-xl transition-all">
                <CardHeader>
                  <CardTitle className="text-2xl text-blue-800">{type.title}</CardTitle>
                  <p className="text-gray-600 mt-2">{type.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {type.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-2">
                        <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Smart Opener Features</h2>
            <p className="text-xl text-gray-600">Modern convenience for Saskatoon homeowners</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {features.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <Card key={idx} className="text-center">
                  <CardHeader>
                    <IconComponent size={48} className="text-blue-700 mx-auto mb-3" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Opener Service in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Professional installation and repair for all opener types serving Saskatoon homeowners.</p>
          <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-green-700 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
            <Phone size={24} className="mr-2" />Call {businessInfo.phone}
          </Button>
          <p className="mt-6 text-green-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default OpenersPage;